/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import { fmtDate, fmtDateTime } from "../lib/format";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { ShieldCheck, Plus, Key, ClockCounterClockwise } from "@phosphor-icons/react";
import { roleLabel as roleLabelFor } from "../lib/rbac";

// Assignable roles shown in the dropdowns. `admin` is hidden from non-admins (managers)
// since the backend blocks a manager from granting/creating admin accounts.
const ROLE_OPTIONS = ["user", "approver", "trainer", "manager", "logistics", "po_dispatch", "finance", "admin"];

export default function Users() {
  const { user: me } = useAuth();
  const canAssignAdmin = me?.role === "admin" || me?.role === "super_admin";
  const roleOptions = ROLE_OPTIONS.filter((r) => r !== "admin" || canAssignAdmin);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ username: "", email: "", role: "user" });
  const [newPin, setNewPin] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkRole, setBulkRole] = useState("logistics");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  const loadAudit = () => api.get("/users/role-audit").then((r) => setAudit(r.data.entries || [])).catch(() => {});
  useEffect(() => { load(); loadAudit(); }, []);

  const create = async () => {
    try {
      const r = await api.post("/users", form);
      setNewPin({ username: form.username, pin: r.data.pin });
      setShowNew(false);
      setForm({ username: "", email: "", role: "user" });
      toast.success("User created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const reissue = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/reissue-pin`);
      setNewPin({ username: u.username, pin: r.data.pin });
      toast.success("PIN re-issued");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const parseBulk = (text) => {
    const rows = [];
    (text || "").split(/\r?\n/).forEach((raw) => {
      const line = raw.trim();
      if (!line) return;
      const parts = line.split(/[,\t;|]+|\s{2,}/).map((p) => p.trim()).filter(Boolean);
      const emailPart = parts.find((p) => p.includes("@")) || "";
      const name = parts.filter((p) => p !== emailPart).join(" ").trim();
      if (emailPart) rows.push({ username: name || emailPart.split("@")[0], email: emailPart.toLowerCase() });
    });
    return rows;
  };

  const bulkAdd = async () => {
    const parsed = parseBulk(bulkText);
    if (!parsed.length) { toast.error("Paste at least one 'Name, email' line"); return; }
    setBulkBusy(true);
    try {
      const r = await api.post("/users/bulk-create-sso", { role: bulkRole, users: parsed });
      setBulkResult(r.data);
      toast.success(`${r.data.created_or_activated} of ${r.data.total} ready for Microsoft sign-in`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBulkBusy(false); }
  };

  const activateSso = async (u) => {    if (!window.confirm(`Activate ${u.email || u.username} for Microsoft sign-in only? They'll be able to log in with Microsoft immediately — no PIN or password needed.`)) return;
    try {
      await api.post(`/users/${u.id}/activate-sso`);
      toast.success(`${u.username} activated for Microsoft SSO`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const activateAllPending = async () => {
    const eligible = users.filter((u) => u.status !== "active" && !u.pin_used);
    if (!window.confirm(`Activate ALL pending password-less accounts for Microsoft sign-in? This flips every pending user to active (Microsoft SSO only). Admin/super-admin rows you can't manage are skipped.`)) return;
    try {
      const r = await api.post("/users/activate-all-pending-sso");
      toast.success(`Activated ${r.data.activated} user(s) for SSO${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}/role`, { role });
      toast.success(`${u.username} is now ${role}`);
      load();
      loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveCompanyEmail = async (u, value) => {
    const next = (value || "").trim().toLowerCase();
    if (next === (u.company_email || "")) return; // no change
    try {
      await api.put(`/users/${u.id}/company-email`, { company_email: next });
      toast.success(next ? `SSO email mapped for ${u.username}` : `SSO email cleared for ${u.username}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      load(); // revert the input to the stored value
    }
  };

  const columns = [
    { key: "username", header: "Username",
      render: (u) => <span className="text-mono" style={{ fontWeight: 700 }}>{u.username}</span> },
    { key: "email", header: "Email" },
    { key: "company_email", header: "Microsoft SSO Email", sortable: false,
      render: (u) => (
        <input
          className="jde-input"
          defaultValue={u.company_email || ""}
          key={u.company_email || ""}
          placeholder="name@rampgroup.co.in"
          data-testid={`sso-email-${u.username}`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          onBlur={(e) => saveCompanyEmail(u, e.target.value)}
          style={{ fontSize: 11, padding: "2px 6px", minWidth: 190 }}
        />
      ) },
    { key: "role", header: "Role",
      render: (u) => (
        <select className="jde-select" value={u.role} onClick={(e) => e.stopPropagation()} onChange={(e) => changeRole(u, e.target.value)} data-testid={`role-select-${u.username}`} style={{ fontSize: 11, padding: "2px 6px" }}>
          {(u.role === "super_admin" ? ["super_admin", ...roleOptions] : roleOptions).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      ) },
    { key: "status", header: "Status",
      render: (u) => <span className={`jde-badge ${u.status === "active" ? "shipped" : "pending"}`}>{u.status}</span> },
    { key: "pin_used", header: "PIN Used",
      accessor: (u) => u.pin_used ? "yes" : "no",
      render: (u) => u.pin_used ? "Yes" : "No" },
    { key: "created_at", header: "Created",
      render: (u) => <span className="muted">{fmtDate(u.created_at)}</span> },
    { key: "actions", header: "Actions", sortable: false,
      render: (u) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {u.status !== "active" && (
            <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={(e) => { e.stopPropagation(); activateSso(u); }} data-testid={`activate-sso-${u.username}`}>
              <ShieldCheck size={11} weight="bold" /> Activate for SSO
            </button>
          )}
          <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); reissue(u); }} data-testid={`reissue-${u.username}`}>
            <Key size={11} weight="bold" /> Re-issue PIN
          </button>
        </div>
      ) },
  ];

  return (
    <Layout breadcrumb={["User Admin"]}>
      <div className="jde-toolbar">
        <ShieldCheck size={14} weight="bold" />
        <span style={{ fontWeight: 700, fontSize: 13 }}>USER ADMINISTRATION</span>
        <div className="divider" />
        <button className="jde-btn jde-btn-primary" onClick={() => setShowNew(true)} data-testid="new-user-btn">
          <Plus size={12} weight="bold" /> New User
        </button>
        <button className="jde-btn jde-btn-sm" onClick={() => { setShowBulk((s) => !s); setBulkResult(null); }} data-testid="bulk-add-toggle" style={{ marginLeft: 8 }}>
          <Plus size={12} weight="bold" /> Bulk Add (SSO)
        </button>
        {users.some((u) => u.status !== "active" && !u.pin_used) && (
          <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={activateAllPending} data-testid="activate-all-pending-sso" style={{ marginLeft: 8 }}>
            <ShieldCheck size={12} weight="bold" /> Activate all pending for SSO ({users.filter((u) => u.status !== "active" && !u.pin_used).length})
          </button>
        )}
        <button className="jde-btn jde-btn-sm" onClick={() => setShowAudit((s) => !s)} data-testid="role-audit-toggle" style={{ marginLeft: 8 }}>
          <ClockCounterClockwise size={12} weight="bold" /> {showAudit ? "Hide" : "Role history"}
          {audit.length ? <span style={{ marginLeft: 6, opacity: 0.7 }}>({audit.length})</span> : null}
        </button>
      </div>

      {showAudit && (
        <div className="jde-panel" style={{ marginTop: 12 }} data-testid="role-audit-panel">
          <div className="jde-panel-header">
            <span>ROLE CHANGE HISTORY</span>
            <button className="jde-btn jde-btn-sm" onClick={loadAudit} data-testid="role-audit-refresh">Refresh</button>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            {audit.length === 0 ? (
              <div className="muted" style={{ padding: 14, fontSize: 12 }}>No role changes recorded yet.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#64748b", background: "#f8fafc" }}>
                    <th style={{ padding: "8px 12px" }}>When</th>
                    <th style={{ padding: "8px 12px" }}>User</th>
                    <th style={{ padding: "8px 12px" }}>Change</th>
                    <th style={{ padding: "8px 12px" }}>By</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a, i) => (
                    <tr key={i} data-testid={`role-audit-row-${i}`} style={{ borderTop: "1px solid #eef2f7" }}>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }} className="muted">
                        {a.at ? fmtDateTime(a.at) : "—"}
                      </td>
                      <td style={{ padding: "8px 12px", fontWeight: 700 }}>{a.target_username || a.target_user_id}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span className="text-mono">{roleLabelFor(a.from_role)}</span>
                        <span style={{ margin: "0 6px", color: "#94a3b8" }}>→</span>
                        <span className="text-mono" style={{ fontWeight: 700 }}>{roleLabelFor(a.to_role)}</span>
                      </td>
                      <td style={{ padding: "8px 12px" }} className="muted">{a.changed_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showBulk && (
        <div className="jde-panel" style={{ marginTop: 12 }} data-testid="bulk-add-panel">
          <div className="jde-panel-header">
            <span>BULK ADD · MICROSOFT SSO</span>
            <button className="jde-btn jde-btn-sm" onClick={() => setShowBulk(false)}>Close</button>
          </div>
          <div className="jde-panel-body">
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Paste one person per line as <strong>Name, email</strong>. They're created + activated for Microsoft sign-in (rampgroup.co.in only) — no PIN or password. Existing people are left as-is.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 200px auto", gap: 10, alignItems: "end" }}>
              <div>
                <label className="jde-label">Name, email (one per line)</label>
                <textarea className="jde-input" rows={6} value={bulkText} onChange={(e) => setBulkText(e.target.value)}
                  placeholder={"Sunil Bisht, sunil.bisht@rampgroup.co.in\nMayank Saxena, mayank.saxena@rampgroup.co.in"}
                  data-testid="bulk-textarea" style={{ fontFamily: "monospace", fontSize: 12, resize: "vertical" }} />
              </div>
              <div>
                <label className="jde-label">Role</label>
                <select className="jde-select" value={bulkRole} onChange={(e) => setBulkRole(e.target.value)} data-testid="bulk-role">
                  {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button className="jde-btn jde-btn-primary" onClick={bulkAdd} disabled={bulkBusy} data-testid="bulk-add-submit">
                {bulkBusy ? "Adding…" : "Add & Activate"}
              </button>
            </div>
            {bulkResult && (
              <div style={{ marginTop: 12 }} data-testid="bulk-result">
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                  {bulkResult.created_or_activated} of {bulkResult.total} ready for Microsoft sign-in
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <tbody>
                    {(bulkResult.results || []).map((r, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #eef2f7" }}>
                        <td style={{ padding: "4px 8px" }}>{r.email}</td>
                        <td style={{ padding: "4px 8px", fontWeight: 700,
                          color: r.status === "error" ? "var(--omh-alert,#b00020)" : (r.status === "skipped" ? "#64748b" : "var(--omh-success,#0a7d33)") }}>
                          {r.status}
                        </td>
                        <td style={{ padding: "4px 8px" }} className="muted">{r.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {newPin && (
        <div className="jde-panel" style={{ marginTop: 12, borderLeft: "4px solid var(--omh-success)" }} data-testid="pin-display">
          <div className="jde-panel-header">
            <span className="success-text">ONE-TIME PIN ISSUED</span>
            <button className="jde-btn jde-btn-sm" onClick={() => setNewPin(null)} data-testid="pin-dismiss">Dismiss</button>
          </div>
          <div className="jde-panel-body">
            <div style={{ fontSize: 12 }} className="muted">Share with user — they enter this on the login screen.</div>
            <div style={{ marginTop: 6, fontSize: 14 }}>User: <strong>{newPin.username}</strong></div>
            <div className="text-mono" style={{ fontSize: 28, fontWeight: 700, color: "var(--omh-primary)", letterSpacing: "0.18em", marginTop: 6 }}>
              {newPin.pin}
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div className="jde-panel" style={{ marginTop: 12 }}>
          <div className="jde-panel-header">
            <span>NEW USER</span>
            <button className="jde-btn jde-btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
          <div className="jde-panel-body">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 200px auto", gap: 10, alignItems: "end" }}>
              <div>
                <label className="jde-label">Username</label>
                <input className="jde-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="new-username" />
              </div>
              <div>
                <label className="jde-label">Email</label>
                <input className="jde-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-email" />
              </div>
              <div>
                <label className="jde-label">Role</label>
                <select className="jde-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-role">
                  {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button className="jde-btn jde-btn-primary" onClick={create} data-testid="new-user-create">Create & Issue PIN</button>
            </div>
          </div>
        </div>
      )}

      <div className="jde-panel" style={{ marginTop: 12 }}>
        <SortableGrid pageSize={100}
          columns={columns}
          rows={users}
          getRowKey={(u) => u.id}
          defaultSort={{ key: "created_at", dir: "desc" }}
          searchPlaceholder="Search username, email, role, status…"
          testid="users-grid"
          statusBar={(visible, all) => (<span>Users: {visible.length}{visible.length !== all.length ? ` of ${all.length}` : ""}</span>)}
        />
      </div>
    </Layout>
  );
}
